import React, { useEffect, useState } from 'react';
import { getTournamentStatistics, TournamentStatistics as TournamentStatsType } from '../../services/statisticsService';
import { formatDistance, formatTime, formatDate } from '../../utils/formatters';
import { 
  FaTrophy, FaUsers, FaRunning, FaRoad, FaClock, FaMedal, FaSpinner, 
  FaExclamationTriangle, FaCalendarAlt, FaInfoCircle, FaChartLine,
  FaFlagCheckered, FaUserFriends, FaStar, FaChartBar, FaMapMarkerAlt
} from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';

interface TournamentStatisticsProps {
  tournamentId: number;
  startDateLocal: string;
  endDate: string;
}

const TournamentStatistics: React.FC<TournamentStatisticsProps> = ({ tournamentId, startDateLocal, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tournamentStats, setTournamentStats] = useState<TournamentStatsType | null>(null);

  // Log để kiểm tra giá trị startDateLocal và endDate
  console.log("TournamentStatistics - Khoảng thời gian:", { tournamentId, startDateLocal, endDate });

  useEffect(() => {
    const fetchTournamentStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getTournamentStatistics(tournamentId, startDateLocal, endDate);
        setTournamentStats(data);
      } catch (err: any) {
        setError(err.message || 'Có lỗi xảy ra khi lấy thống kê giải đấu');
      } finally {
        setLoading(false);
      }
    };

    if (tournamentId) {
      fetchTournamentStats();
    }
  }, [tournamentId, startDateLocal, endDate]);

  // Hàm chuyển đổi màu sắc dựa vào theme
  const getColor = (color: string): string => {
    if (theme === 'dark') {
      // Thêm độ sáng cho màu khi ở dark mode
      return color === '#20757A' ? '#5dbec3' :
             color === '#C1322D' ? '#e85853' :
             color === '#6A5ACD' ? '#9f8afa' :
             color === '#2E8B57' ? '#4eaf7b' :
             color === '#FF8C00' ? '#ffac4d' :
             color;
    }
    return color;
  };
  
  // Tạo thanh tiến trình
  const ProgressBar = ({ 
    value, 
    maxValue, 
    color, 
    height = 8,
    label,
    showValue = true 
  }: { 
    value: number, 
    maxValue: number, 
    color: string,
    height?: number,
    label?: string,
    showValue?: boolean 
  }) => {
    const percent = maxValue > 0 ? (value / maxValue) * 100 : 0;
    return (
      <div style={{ marginBottom: '15px' }}>
        {label && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '5px',
            fontSize: '0.9rem',
            color: theme === 'dark' ? '#bbb' : '#666'
          }}>
            <span>{label}</span>
            {showValue && <span>{typeof value === 'number' ? formatDistance(value) : value}</span>}
          </div>
        )}
        <div style={{ 
          width: '100%', 
          backgroundColor: theme === 'dark' ? '#444' : '#f0f0f0',
          borderRadius: '4px',
          height: `${height}px`,
          overflow: 'hidden'
        }}>
          <div style={{ 
            width: `${percent}%`, 
            backgroundColor: getColor(color),
            height: '100%',
            borderRadius: '4px',
            transition: 'width 1s ease-in-out'
          }} />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-state">
        <FaSpinner className="icon-spin" size={40} color="#20757A" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <FaExclamationTriangle size={20} />
        <span>{error}</span>
      </div>
    );
  }

  if (!tournamentStats) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <FaTrophy />
        </div>
        <div className="empty-state-text">Không có dữ liệu thống kê cho giải đấu</div>
      </div>
    );
  }

  // Tính tổng quãng đường trung bình mỗi người
  const avgDistancePerUser = tournamentStats.userCount > 0 
    ? tournamentStats.totalDistance / tournamentStats.userCount 
    : 0;

  // Tính số hoạt động trung bình mỗi người
  const avgActivitiesPerUser = tournamentStats.userCount > 0 
    ? tournamentStats.activityCount / tournamentStats.userCount 
    : 0;

  return (
    <div className={`tournament-statistics ${theme}`}>
      {/* Thông tin tổng quan về giải đấu */}
      <div className={`statistics-card ${theme}`}>
        <div className="statistics-card-header">
          <div className="statistics-card-title">
            <FaTrophy /> {tournamentStats ? tournamentStats.tournamentName : 'Thống kê giải đấu'}
          </div>
        </div>
        
        <div className="tournament-details" style={{ 
          padding: '0 20px 20px', 
          borderBottom: theme === 'dark' ? '1px solid #444' : '1px solid #eee',
          marginBottom: '20px'
        }}>
          <div style={{ 
            fontSize: '1rem', 
            color: theme === 'dark' ? '#bbb' : '#555',
            marginBottom: '15px', 
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px'
          }}>
            <FaInfoCircle style={{ marginTop: '4px' }} />
            <div>{tournamentStats.description}</div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '15px',
            color: theme === 'dark' ? '#bbb' : '#666',
            fontSize: '0.95rem',
            marginBottom: '15px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <FaCalendarAlt color={getColor('#20757A')} />
              <span>Bắt đầu: <strong style={{ color: theme === 'dark' ? '#ddd' : '#333' }}>{formatDate(tournamentStats.startDate)}</strong></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <FaFlagCheckered color={getColor('#C1322D')} />
              <span>Kết thúc: <strong style={{ color: theme === 'dark' ? '#ddd' : '#333' }}>{formatDate(tournamentStats.endDate)}</strong></span>
            </div>
          </div>
        </div>
        
        {/* Hiển thị số liệu chính dạng card */}
        <div className="statistics-metrics" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '15px',
          padding: '0 20px 20px'
        }}>
          <div className="metric-card" style={{ 
            backgroundColor: theme === 'dark' ? 'rgba(32, 117, 122, 0.1)' : 'rgba(32, 117, 122, 0.05)',
            borderLeft: `4px solid ${getColor('#20757A')}`,
            padding: '15px',
            borderRadius: '4px'
          }}>
            <div style={{ fontSize: '0.9rem', color: theme === 'dark' ? '#bbb' : '#666', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <FaUsers /> Người tham gia
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: getColor('#20757A') }}>
              {tournamentStats.userCount}
            </div>
          </div>
          
          <div className="metric-card" style={{ 
            backgroundColor: theme === 'dark' ? 'rgba(193, 50, 45, 0.1)' : 'rgba(193, 50, 45, 0.05)',
            borderLeft: `4px solid ${getColor('#C1322D')}`,
            padding: '15px',
            borderRadius: '4px'
          }}>
            <div style={{ fontSize: '0.9rem', color: theme === 'dark' ? '#bbb' : '#666', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <FaUserFriends /> Đội tham gia
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: getColor('#C1322D') }}>
              {tournamentStats.teamCount}
            </div>
          </div>
          
          <div className="metric-card" style={{ 
            backgroundColor: theme === 'dark' ? 'rgba(106, 90, 205, 0.1)' : 'rgba(106, 90, 205, 0.05)',
            borderLeft: `4px solid ${getColor('#6A5ACD')}`,
            padding: '15px',
            borderRadius: '4px'
          }}>
            <div style={{ fontSize: '0.9rem', color: theme === 'dark' ? '#bbb' : '#666', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <FaRunning /> Hoạt động
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: getColor('#6A5ACD') }}>
              {tournamentStats.activityCount}
            </div>
          </div>
          
          <div className="metric-card" style={{ 
            backgroundColor: theme === 'dark' ? 'rgba(46, 139, 87, 0.1)' : 'rgba(46, 139, 87, 0.05)',
            borderLeft: `4px solid ${getColor('#2E8B57')}`,
            padding: '15px',
            borderRadius: '4px'
          }}>
            <div style={{ fontSize: '0.9rem', color: theme === 'dark' ? '#bbb' : '#666', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <FaRoad /> Tổng quãng đường
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: getColor('#2E8B57') }}>
              {formatDistance(tournamentStats.totalDistance)}
            </div>
          </div>
        </div>
      </div>

      {/* Hiển thị phân tích số liệu */}
      <div className={`statistics-card ${theme}`}>
        <div className="statistics-card-header">
          <h2 className="statistics-card-title">
            <FaChartBar /> Phân tích chi tiết
          </h2>
        </div>
        
        <div className="detailed-stats" style={{ padding: '20px' }}>
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', color: theme === 'dark' ? '#ddd' : '#333' }}>
              <FaUsers style={{ marginRight: '8px' }} />
              Hoạt động người dùng
            </h3>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '20px',
              marginBottom: '15px' 
            }}>
              <div>
                <div style={{ fontSize: '0.9rem', marginBottom: '5px', color: theme === 'dark' ? '#bbb' : '#666' }}>
                  Quãng đường trung bình mỗi người
                </div>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    backgroundColor: getColor('#20757A'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    <FaRoad />
                  </div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: getColor('#20757A') }}>
                    {formatDistance(avgDistancePerUser)}
                  </div>
                </div>
              </div>
              
              <div>
                <div style={{ fontSize: '0.9rem', marginBottom: '5px', color: theme === 'dark' ? '#bbb' : '#666' }}>
                  Hoạt động trung bình mỗi người
                </div>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    backgroundColor: getColor('#6A5ACD'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    <FaRunning />
                  </div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: getColor('#6A5ACD') }}>
                    {avgActivitiesPerUser.toFixed(1)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top đội và cá nhân xuất sắc */}
      <div className="stats-top-performers" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '20px',
        marginTop: '20px'
      }}>
        {tournamentStats.topTeam && (
          <div className={`statistics-card ${theme}`}>
            <div className="statistics-card-header">
              <h2 className="statistics-card-title">
                <FaMedal /> Đội xuất sắc nhất
              </h2>
            </div>
            
            <div className="top-team-card" style={{ padding: '20px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '15px' 
              }}>
                <div style={{ 
                  width: '60px', 
                  height: '60px', 
                  borderRadius: '50%', 
                  backgroundColor: getColor('#20757A'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '24px',
                  marginRight: '15px',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                  border: theme === 'dark' ? '2px solid #444' : '2px solid #fff'
                }}>
                  {tournamentStats.topTeam.teamName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 style={{ 
                    margin: '0 0 5px 0', 
                    color: theme === 'dark' ? '#ddd' : '#333',
                    fontSize: '1.3rem'
                  }}>
                    {tournamentStats.topTeam.teamName}
                  </h3>
                  <div style={{ 
                    color: theme === 'dark' ? '#bbb' : '#666',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <FaStar color="#FFD700" style={{ marginRight: '5px' }} />
                    Đứng đầu giải đấu
                  </div>
                </div>
              </div>
              
              <div style={{ 
                backgroundColor: theme === 'dark' ? 'rgba(32, 117, 122, 0.1)' : 'rgba(32, 117, 122, 0.05)',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px'
              }}>
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '15px'
                }}>
                  <div>
                    <div style={{ 
                      color: theme === 'dark' ? '#bbb' : '#666',
                      fontSize: '0.8rem',
                      marginBottom: '5px'
                    }}>
                      Quãng đường
                    </div>
                    <div style={{ 
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      color: getColor('#20757A'),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <FaRoad size={12} />
                      {formatDistance(tournamentStats.topTeam.totalDistance)}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ 
                      color: theme === 'dark' ? '#bbb' : '#666',
                      fontSize: '0.8rem',
                      marginBottom: '5px'
                    }}>
                      Thành viên
                    </div>
                    <div style={{ 
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      color: getColor('#C1322D'),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <FaUserFriends size={12} />
                      {tournamentStats.topTeam.userCount}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ 
                      color: theme === 'dark' ? '#bbb' : '#666',
                      fontSize: '0.8rem',
                      marginBottom: '5px'
                    }}>
                      Hoạt động
                    </div>
                    <div style={{ 
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      color: getColor('#6A5ACD'),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <FaRunning size={12} />
                      {tournamentStats.topTeam.activityCount}
                    </div>
                  </div>
                </div>
              </div>
              
              {tournamentStats.topTeam.topUser && (
                <div style={{ 
                  backgroundColor: theme === 'dark' ? 'rgba(255, 140, 0, 0.1)' : 'rgba(255, 140, 0, 0.05)',
                  padding: '15px',
                  borderRadius: '8px',
                  marginTop: '15px'
                }}>
                  <div style={{ 
                    fontWeight: 'bold', 
                    marginBottom: '10px',
                    color: '#FF8C00',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <FaMedal /> Thành viên xuất sắc nhất
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {tournamentStats.topTeam.topUser.avatarUrl ? (
                      <img 
                        src={tournamentStats.topTeam.topUser.avatarUrl} 
                        alt={tournamentStats.topTeam.topUser.fullName}
                        style={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '50%',
                          border: '2px solid #FF8C00'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#FF8C00',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold'
                      }}>
                        {tournamentStats.topTeam.topUser.fullName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{ fontSize: '0.95rem', fontWeight: 'bold' }}>
                      {tournamentStats.topTeam.topUser.fullName}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tournamentStats.topUser && (
          <div className={`statistics-card ${theme}`}>
            <div className="statistics-card-header">
              <h2 className="statistics-card-title">
                <FaUsers /> Cá nhân xuất sắc nhất
              </h2>
            </div>
            
            <div className="top-user-card" style={{ padding: '20px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '15px' 
              }}>
                {tournamentStats.topUser.avatarUrl ? (
                  <img 
                    src={tournamentStats.topUser.avatarUrl} 
                    alt={tournamentStats.topUser.fullName}
                    style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%',
                      marginRight: '15px',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                      border: theme === 'dark' ? '2px solid #444' : '2px solid #fff'
                    }}
                  />
                ) : (
                  <div style={{ 
                    width: '60px', 
                    height: '60px', 
                    borderRadius: '50%', 
                    backgroundColor: getColor('#6A5ACD'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '24px',
                    marginRight: '15px',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    border: theme === 'dark' ? '2px solid #444' : '2px solid #fff'
                  }}>
                    {tournamentStats.topUser.fullName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 style={{ 
                    margin: '0 0 5px 0', 
                    color: theme === 'dark' ? '#ddd' : '#333',
                    fontSize: '1.3rem'
                  }}>
                    {tournamentStats.topUser.fullName}
                  </h3>
                  <div style={{ 
                    color: theme === 'dark' ? '#bbb' : '#666',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <FaStar color="#FFD700" style={{ marginRight: '5px' }} />
                    Người dẫn đầu giải đấu
                  </div>
                </div>
              </div>
              
              <div style={{ 
                backgroundColor: theme === 'dark' ? 'rgba(32, 117, 122, 0.1)' : 'rgba(32, 117, 122, 0.05)',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px'
              }}>
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '15px'
                }}>
                  <div>
                    <div style={{ 
                      color: theme === 'dark' ? '#bbb' : '#666',
                      fontSize: '0.8rem',
                      marginBottom: '5px'
                    }}>
                      Quãng đường
                    </div>
                    <div style={{ 
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      color: getColor('#20757A'),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <FaRoad size={12} />
                      {formatDistance(tournamentStats.topUser.totalDistance)}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ 
                      color: theme === 'dark' ? '#bbb' : '#666',
                      fontSize: '0.8rem',
                      marginBottom: '5px'
                    }}>
                      Hoạt động 
                    </div>
                    <div style={{ 
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      color: getColor('#6A5ACD'),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <FaRunning size={12} />
                      {tournamentStats.topUser.activityCount}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ 
                      color: theme === 'dark' ? '#bbb' : '#666',
                      fontSize: '0.8rem',
                      marginBottom: '5px'
                    }}>
                      Thời gian
                    </div>
                    <div style={{ 
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      color: getColor('#2E8B57'),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <FaClock size={12} />
                      {formatTime(tournamentStats.topUser.totalMovingTime)}
                    </div>
                  </div>
                </div>
              </div>
              
              {tournamentStats.topUser.lastActivityDate && (
                <div style={{ 
                  backgroundColor: theme === 'dark' ? 'rgba(255, 140, 0, 0.1)' : 'rgba(255, 140, 0, 0.05)',
                  padding: '15px',
                  borderRadius: '8px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '5px',
                    color: '#FF8C00',
                    fontSize: '0.9rem',
                    marginBottom: '5px'
                  }}>
                    <FaMapMarkerAlt /> Hoạt động gần nhất
                  </div>
                  <div style={{ 
                    fontSize: '0.95rem',
                    color: theme === 'dark' ? '#ddd' : '#333'
                  }}>
                    {tournamentStats.topUser.lastActivityDate ? formatDate(tournamentStats.topUser.lastActivityDate) : 'Chưa có hoạt động'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentStatistics; 